import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryKeys } from '@/api/query-client';
import type { paths } from '@/api/generated/schema';

/**
 * Les types de paramètres sont DÉRIVÉS du document OpenAPI plutôt que réécrits à la main
 * (même convention que `frontend/src/api/queries/dashboard.ts`) : ajouter un filtre côté
 * C# le rend disponible ici sans rien retoucher, et en retirer un casse la compilation du
 * front au lieu de produire un paramètre silencieusement ignoré par l'API.
 */
type QueryOf<TPath extends keyof paths> = NonNullable<
  paths[TPath] extends { get: { parameters: { query?: infer TQuery } } } ? TQuery : never
>;

export type TenantsQuery = QueryOf<'/api/v1/admin/tenants'>;

/** Liste paginée des tenants, tous statuts confondus (vue admin). */
export function useTenants(params: TenantsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.tenants(params),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/tenants', { params: { query: params } });

      return data!;
    },
  });
}

/** Détail d'un tenant : infos, nombre d'utilisateurs, abonnement actif. */
export function useTenantDetail(tenantId: string) {
  return useQuery({
    queryKey: queryKeys.tenant(tenantId),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/tenants/{tenantId}', {
        params: { path: { tenantId } },
      });

      return data!;
    },

    enabled: tenantId.length > 0,
  });
}

/**
 * Les trois mutations de cycle de vie invalident la liste ET le détail du tenant concerné :
 * la liste affiche le badge de statut, le détail affiche le motif de suspension — les deux
 * doivent refléter l'action tout de suite après confirmation.
 */
function useTenantLifecycleMutation(
  action: 'suspend' | 'activate' | 'cancel',
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, reason }: { tenantId: string; reason?: string }) => {
      if (action === 'suspend') {
        await api.POST('/api/v1/admin/tenants/{tenantId}/suspend', {
          params: { path: { tenantId } },
          body: { reason: reason ?? '' },
        });

        return;
      }

      if (action === 'activate') {
        await api.POST('/api/v1/admin/tenants/{tenantId}/activate', {
          params: { path: { tenantId } },
          // Le contrat OpenAPI ne déclare aucun corps pour cette route (la commande ne lie
          // que l'id de route), mais FastEndpoints exige quand même un `Content-Type` et un
          // corps JSON syntaxiquement valide au niveau transport, sans quoi il répond 415
          // avant même d'atteindre le binding. Un objet vide suffit ; openapi-fetch pose
          // alors lui-même le bon `Content-Type`.
          // @ts-expect-error — voir commentaire ci-dessus.
          body: {},
        });

        return;
      }

      await api.POST('/api/v1/admin/tenants/{tenantId}/cancel', {
        params: { path: { tenantId } },
        // @ts-expect-error — voir le commentaire sur la branche « activate » ci-dessus.
        body: {},
      });
    },

    onSuccess: async (_data, { tenantId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.tenants() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) }),
      ]);
    },
  });
}

export function useSuspendTenant() {
  return useTenantLifecycleMutation('suspend');
}

export function useActivateTenant() {
  return useTenantLifecycleMutation('activate');
}

export function useCancelTenant() {
  return useTenantLifecycleMutation('cancel');
}

/**
 * Offre au tenant un abonnement de 500 écrans, sans paiement. Refusé par l'API en production
 * (`subscription.complimentary_disabled`) : le bouton n'est affiché que si la fiche l'autorise.
 */
export function useGrantComplimentarySubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId }: { tenantId: string }) => {
      await api.POST('/api/v1/admin/tenants/{tenantId}/complimentary-subscription', {
        params: { path: { tenantId } },
        // @ts-expect-error — corps vide exigé par FastEndpoints, voir la branche « activate » ci-dessus.
        body: {},
      });
    },

    onSuccess: async (_data, { tenantId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) });
    },
  });
}

/** Toutes les offres, actives et inactives (vue admin — la vue cliente ne montre que les actives). */
export function usePlans() {
  return useQuery({
    queryKey: queryKeys.plans,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/plans');

      return data!;
    },
  });
}

type CreatePlanBody = NonNullable<
  paths['/api/v1/admin/plans']['post']['requestBody']
>['content']['application/json'];

export function useCreatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreatePlanBody) => {
      const { data } = await api.POST('/api/v1/admin/plans', { body: payload });

      return data!;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.plans });
    },
  });
}

type UpdatePlanBody = NonNullable<
  paths['/api/v1/admin/plans/{planId}']['put']['requestBody']
>['content']['application/json'];

export function useUpdatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ planId, ...payload }: UpdatePlanBody & { planId: string }) => {
      await api.PUT('/api/v1/admin/plans/{planId}', {
        params: { path: { planId } },
        body: payload,
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.plans });
    },
  });
}

export function useSetPlanStripePriceId() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ planId, stripePriceId }: { planId: string; stripePriceId: string | null }) => {
      await api.PUT('/api/v1/admin/plans/{planId}/stripe-price', {
        params: { path: { planId } },
        body: { stripePriceId },
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.plans });
    },
  });
}

function usePlanActivationMutation(action: 'activate' | 'deactivate') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (planId: string) => {
      await api.POST(
        action === 'activate'
          ? '/api/v1/admin/plans/{planId}/activate'
          : '/api/v1/admin/plans/{planId}/deactivate',
        // Voir le commentaire sur `useTenantLifecycleMutation` : corps vide nécessaire
        // au niveau transport même si le contrat OpenAPI n'en déclare aucun.
        // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
        { params: { path: { planId } }, body: {} },
      );
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.plans });
    },
  });
}

export function useActivatePlan() {
  return usePlanActivationMutation('activate');
}

export function useDeactivatePlan() {
  return usePlanActivationMutation('deactivate');
}

// ============================================================================
// Utilisateurs (lot 2 — comptes back-office et utilisateurs de tenant)
// ============================================================================

export type PlatformAdminsQuery = QueryOf<'/api/v1/admin/users'>;

/** Liste paginée des comptes `PlatformAdmin` (équipe Pixlide). */
export function usePlatformAdmins(params: PlatformAdminsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.platformAdmins,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/users', { params: { query: params } });

      return data!;
    },
  });
}

type CreatePlatformAdminBody = NonNullable<
  paths['/api/v1/admin/users']['post']['requestBody']
>['content']['application/json'];

/** Création d'un nouveau compte back-office. */
export function useCreatePlatformAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreatePlatformAdminBody) => {
      const { data } = await api.POST('/api/v1/admin/users', { body: payload });

      return data!;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.platformAdmins });
    },
  });
}

type PromotePlatformAdminBody = NonNullable<
  paths['/api/v1/admin/users/promote']['post']['requestBody']
>['content']['application/json'];

/**
 * Promotion d'un compte EXISTANT en compte back-office.
 *
 * Contrepartie obligée de `useCreatePlatformAdmin` : l'email identifie un compte de façon
 * unique sur toute la plateforme, donc dès que l'adresse est prise la création répond 409 et
 * il n'existe aucun second compte possible sur cette adresse — seule la promotion de celui
 * qui existe donne l'accès back-office.
 */
export function usePromotePlatformAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PromotePlatformAdminBody) => {
      const { data } = await api.POST('/api/v1/admin/users/promote', { body: payload });

      return data!;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.platformAdmins });
    },
  });
}

/**
 * Suppression d'un compte — back-office ou client. Réservée au super admin (`platform:super-admin`).
 *
 * Deux routes pour une même commande côté API : chacune vit dans le groupe de l'écran qui
 * l'appelle. Contrairement aux POST sans corps plus haut, un DELETE n'a pas le piège 415 :
 * FastEndpoints n'exige un corps JSON que sur les verbes qui en portent un.
 */
type DeleteUserVariables = { scope: 'team'; userId: string } | { scope: 'client'; userId: string; tenantId: string };

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: DeleteUserVariables) => {
      const params = { params: { path: { userId: variables.userId } } };

      if (variables.scope === 'team') {
        await api.DELETE('/api/v1/admin/users/{userId}', params);

        return;
      }

      await api.DELETE('/api/v1/admin/tenant-users/{userId}', params);
    },

    onSuccess: async (_data, variables) => {
      if (variables.scope === 'team') {
        await queryClient.invalidateQueries({ queryKey: queryKeys.platformAdmins });

        return;
      }

      // Même utilisateur visible sur la liste transverse ET sur la fiche de son tenant.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.platformUsers() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.tenantUsers(variables.tenantId) }),
      ]);
    },
  });
}

/** Liste paginée des utilisateurs d'un tenant donné. */
export function useTenantUsers(tenantId: string) {
  return useQuery({
    queryKey: queryKeys.tenantUsers(tenantId),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/tenants/{tenantId}/users', {
        params: { path: { tenantId } },
      });

      return data!;
    },

    enabled: tenantId.length > 0,
  });
}

/**
 * Activation/désactivation d'un utilisateur de tenant — même piège transport que
 * `useTenantLifecycleMutation` ci-dessus : ces routes ne déclarent aucun corps dans le
 * contrat OpenAPI, mais FastEndpoints répond 415 sans `Content-Type` + corps JSON valide.
 */
function useTenantUserStatusMutation(action: 'disable' | 'enable') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, userId }: { tenantId: string; userId: string }) => {
      await api.POST(
        action === 'disable'
          ? '/api/v1/admin/tenants/{tenantId}/users/{userId}/disable'
          : '/api/v1/admin/tenants/{tenantId}/users/{userId}/enable',
        {
          params: { path: { tenantId, userId } },
          // @ts-expect-error — voir le commentaire sur `useTenantLifecycleMutation` plus haut.
          body: {},
        },
      );
    },

    onSuccess: async (_data, { tenantId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.tenantUsers(tenantId) }),
        // La liste transverse affiche le même utilisateur : la laisser sur son statut
        // précédent afficherait deux vérités contradictoires selon l'écran ouvert.
        queryClient.invalidateQueries({ queryKey: queryKeys.platformUsers() }),
      ]);
    },
  });
}

export function useDisableTenantUser() {
  return useTenantUserStatusMutation('disable');
}

export function useEnableTenantUser() {
  return useTenantUserStatusMutation('enable');
}

// ============================================================================
// Utilisateurs clients, vue transverse (écran « Utilisateurs » du back-office)
// ============================================================================

export type PlatformUsersQuery = QueryOf<'/api/v1/admin/tenant-users'>;

/** Liste paginée des utilisateurs de TOUS les tenants clients (comptes back-office exclus). */
export function usePlatformUsers(params: PlatformUsersQuery = {}) {
  return useQuery({
    queryKey: queryKeys.platformUsers(params),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/tenant-users', { params: { query: params } });

      return data!;
    },
  });
}

type InviteTenantUserBody = NonNullable<
  paths['/api/v1/admin/tenant-users/invite']['post']['requestBody']
>['content']['application/json'];

/** Invitation d'un utilisateur dans un tenant client, depuis le back-office. */
export function useInviteTenantUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: InviteTenantUserBody) => {
      const { data } = await api.POST('/api/v1/admin/tenant-users/invite', { body: payload });

      return data!;
    },

    // Rien à invalider côté liste : l'invitation ne crée pas encore de compte, l'utilisateur
    // n'apparaîtra qu'une fois l'invitation acceptée. On rafraîchit tout de même la fiche du
    // tenant concerné, qui affiche le nombre d'utilisateurs.
    onSuccess: async (_data, { tenantId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) });
    },
  });
}

/**
 * Envoi d'un lien de réinitialisation de mot de passe à un utilisateur.
 *
 * Aucune invalidation : rien de ce qui est affiché ne change (le compte reste actif, avec le
 * même mot de passe tant que le lien n'est pas utilisé).
 */
export function useSendUserPasswordReset() {
  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      await api.POST('/api/v1/admin/tenant-users/{userId}/password-reset', {
        params: { path: { userId } },
        // @ts-expect-error — voir le commentaire sur `useTenantLifecycleMutation` plus haut.
        body: {},
      });
    },
  });
}

/** Retrait de la double authentification d'un compte client bloqué. */
export function useDisableUserTwoFactor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      await api.POST('/api/v1/admin/tenant-users/{userId}/disable-two-factor', {
        params: { path: { userId } },
        // @ts-expect-error — voir le commentaire sur `useTenantLifecycleMutation` plus haut.
        body: {},
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.platformUsers() });
    },
  });
}
